import { orderStatusConstants, orderTypeConstants } from "constants/order";
import { orderHsitoryTypeConstants } from "constants/orderHistory";
import { ToastContext } from "context/ToastContext";
import { useUserWithAccount } from "context/UserAccountProvider";
import { parseDate, parseTime } from "helpers/date";
import { calculateClearPatientFee } from "helpers/fee";
import { centsToDollars, parsePrice, dollarsToCents } from "helpers/price";
import { sizer } from "layout/styles/styled/sizer";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Timeline } from "primereact/timeline";
import { useMemo, useState, useContext } from "react";
import { useUpdateOrderMutation } from "store/queries/order";
import { useCreateOrderHistoryAsStaffMutation } from "store/queries/orderHistory";
import styled from "styled-components";
import { IBenefitValueChange, IOrderHistory } from "types/Order/Order";
import { OrderType } from "types/Order/OrderType";
import { IProviderAmount, IPaymentPlan } from "types/Payment";
import { SpecialtyList } from "types/Provider/Provider";

import { AddNoteDialog, IPaymentNoteData } from "./components/AddNoteDialog";
import {
  getProviderReduceByAmount,
  getReducedRemainingInCents,
} from "./helpers";

const StyledCard = styled(Card)`
  margin-bottom: ${sizer(2)};
  border: 1px solid var(--gray-new-200) !important;
`;

const StyledTimeline = styled(Timeline)`
  .p-timeline-event-opposite {
    flex: 0 0;
    min-width: 178px;
    padding-top: 6px !important;
  }
  .p-timeline-event-content {
    padding-top: 6px !important;
  }
`;

const StyledShowMore = styled.p`
  color: var(--color-purple);
  margin-top: 30px;
`;

const descriptionRenderer: { [key: string]: string } = {
  Message1: "Message 1",
  Message2: "Message 2",
  Message3: "Message 3",
  Receipt: "Receipt",
};

const HST_ROBOT_EMAIL = "support+hst@clearhealthinc.com";

const StyledMessageBody = styled.div`
  margin-top: 4px;
  padding-left: 8px;
  border-left: 2px solid var(--gray-new-200);
  color: var(--gray-new-600);
  white-space: pre-wrap;
  word-break: break-word;
`;

const StyledViewMessage = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin-left: 4px;
  color: var(--color-purple);
  cursor: pointer;
  font: inherit;
`;

// CLE-2576 — the text we actually sent the patient lives on the linked Communications
// row, not on the history row. Only present for messages sent after the link was added,
// so older history rows render nothing at all.
function SentMessageBody({ item }: { item: IOrderHistory }) {
  const [expanded, setExpanded] = useState(false);
  const body = item.communication?.communicationRequest?.body;
  if (!body) return null;
  return (
    <>
      <StyledViewMessage type="button" onClick={() => setExpanded(!expanded)}>
        {expanded ? "Hide message" : "View message"}
      </StyledViewMessage>
      {expanded && (
        <StyledMessageBody data-pii="message-body">{body}</StyledMessageBody>
      )}
    </>
  );
}

const hstUpdateDescriptionBySyncType: { [key: string]: string } = {
  ESTIMATE: "amount due inserted",
  PAYMENT: "payment recorded",
  REDONE: "amount due updated",
  ESTIMATE_OPENED_NOTE: "estimate opened note added",
  MANUAL_NOTE_ADDED: "manual note added",
};

// CLE-2520 — one benefit line of an automatic eligibility re-run, e.g.
// "Deductible from $100.00 to $160.00". Coinsurance is a percent, everything else cents.
const formatBenefitChangeValue = (change: IBenefitValueChange, value: number) =>
  change.unit === "percent" ? `${value}%` : parsePrice(centsToDollars(value));

const renderBenefitChanges = (
  label: string,
  changes: IBenefitValueChange[]
) => {
  if (!changes.length) return null;
  return (
    <div className="mt-1">
      {label}
      {changes.map((change) => (
        <div key={`${label}_${change.field}`} className="ml-2">
          {change.label} from{" "}
          {formatBenefitChangeValue(change, change.previousValue)} to{" "}
          {formatBenefitChangeValue(change, change.newValue)}
        </div>
      ))}
    </div>
  );
};

const renderEligibilityRerunChanges = (item: IOrderHistory) => {
  const changes = item.benefitValueChanges;
  if (!changes) return null;

  const { amountInCents, patientResponsibilityEstimateAmountInCents } =
    changes.prices;
  const amountChanged = amountInCents.previousValue !== amountInCents.newValue;
  const responsibilityChanged =
    patientResponsibilityEstimateAmountInCents.previousValue !==
    patientResponsibilityEstimateAmountInCents.newValue;

  return (
    <>
      {renderBenefitChanges("Primary insurance:", changes.benefits.primary)}
      {renderBenefitChanges("Secondary insurance:", changes.benefits.secondary)}
      {(amountChanged || responsibilityChanged) && (
        <div className="mt-1">
          Price:
          {amountChanged && (
            <div className="ml-2">
              Clear price offered to patient{" "}
              {parsePrice(centsToDollars(amountInCents.previousValue))} to{" "}
              {parsePrice(centsToDollars(amountInCents.newValue))}
            </div>
          )}
          {responsibilityChanged && (
            <div className="ml-2">
              Total estimated patient cost from{" "}
              {parsePrice(
                centsToDollars(
                  patientResponsibilityEstimateAmountInCents.previousValue
                )
              )}{" "}
              to{" "}
              {parsePrice(
                centsToDollars(
                  patientResponsibilityEstimateAmountInCents.newValue
                )
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export const generateOrderHistoryLine = (
  item: IOrderHistory,
  patientName: string
) => {
  const isPaid = !!(item.type === "Paid");
  const isPartiallyPaid = !!(item.type === "PartiallyPaid");
  const isInstallmentPaid = !!(item.type === "InstallmentPaid");
  const isPaidAdditionalAmount = !!(
    item.type === orderHsitoryTypeConstants.PaidAdditionalAmount
  );
  const isExternalPaymentRecorded = !!(
    item.type === orderHsitoryTypeConstants.ExternalPaymentRecorded
  );
  const isCommunicationSent = !!(
    item.type === "EmailSent" ||
    item.type === "TextMessageSent" ||
    item.type === "EmailResent" ||
    item.type === "TextMessageResent"
  );
  const isTransfer = item.type === "Transfer";
  // CLE-2771: a failed transfer reads exactly like a successful one — same "$X From A To B" —
  // so staff can see at a glance that this exact transfer has already been tried and refused.
  const isTransferFailed = item.type === "TransferFailed";
  // ...but only when there was a transfer to describe. writeFailedTransferHistory falls back to a
  // single party-less, amount-less row when the payload had nothing to itemise, and rendering that
  // through the transfer text would claim a "$0.00 From Clear Health To Clear Health" transfer that
  // never existed. Those rows show the reason alone.
  const isItemisedTransferFailure =
    isTransferFailed &&
    (item.amountInCents !== null || !!item.fromProvider || !!item.toProvider);
  const isBalanceUpdated = item.type === "BalanceUpdated";
  const isBalanceReported = item.type === "BalanceReported";
  const isProviderPriorityUpdated = item.type === "ProviderPriorityUpdated";
  const isTransferBlocked = !!(
    item.type === "PaymentMarkedBlockedForTransfers"
  );
  const isTransferReady = !!(item.type === "PaymentMarkedReadyForTransfers");
  const isUpdated = item.type === "Updated";
  const isEligibilityRerun = item.type === "EligibilityRerun";
  // CLE-2803 — the flag the monthly eligibility check writes; it carries only a description.
  const isCoverageInactive = item.type === "CoverageInactive";
  const isHstUpdate = item.type === "HstUpdate";
  const hstUpdateDescription =
    isHstUpdate && item.hstSync?.syncType
      ? hstUpdateDescriptionBySyncType[item.hstSync.syncType]
      : "";

  const isPatientRecipient = !!(
    item.toPatient?.firstName && item.toPatient?.lastName
  );
  const toName =
    item.toProvider?.name ||
    (isPatientRecipient
      ? `${item.toPatient?.firstName} ${item.toPatient?.lastName}`
      : "Clear Health");
  const transferText = (
    <>
      {` ${parsePrice(centsToDollars(item.amountInCents || 0))} From ${
        item.fromProvider?.name || "Clear Health"
      } To `}
      {isPatientRecipient ? (
        <span data-pii="patient-name">{toName}</span>
      ) : (
        toName
      )}
    </>
  );
  return (
    <div>
      {parseDate(item.createdAt, false, true)} {parseTime(item.createdAt, true)}{" "}
      {(isTransfer || isItemisedTransferFailure) && transferText}
      {(isPartiallyPaid ||
        isInstallmentPaid ||
        isPaidAdditionalAmount ||
        isExternalPaymentRecorded) &&
        item.amountInCents !== null &&
        item.amountInCents !== undefined &&
        ` Amount ${parsePrice(centsToDollars(item.amountInCents))}`}{" "}
      {isExternalPaymentRecorded &&
        item.externalPaymentMethod &&
        item.externalPaymentMethod !== "CashUnmanaged" &&
        ` Payment Type: ${
          item.externalPaymentMethod === "CareCredit"
            ? "Financing"
            : item.externalPaymentMethod
        }`}{" "}
      {item.user &&
        !isPaid &&
        !isPartiallyPaid &&
        !isPaidAdditionalAmount &&
        `by: ${item.user.name}`}
      {isHstUpdate && !item.user && `by ${HST_ROBOT_EMAIL}`}
      {isHstUpdate && item.user && `by ${item.user.name}`}
      {isHstUpdate && hstUpdateDescription && `, ${hstUpdateDescription}`}
      {item.user &&
        !isHstUpdate &&
        (isPaid || isPartiallyPaid || isPaidAdditionalAmount) &&
        ` Collected by ${item.user.name}`}{" "}
      {(isPaid || isPartiallyPaid || isPaidAdditionalAmount) &&
        !isHstUpdate &&
        ` Paid by ${patientName}`}{" "}
      {isBalanceReported && item.user && ` Reported by ${item.user.name}`}{" "}
      {isBalanceReported && !item.user && (
        <>
          {" Reported by "}
          <span data-pii="patient-name">{patientName}</span>
        </>
      )}{" "}
      {item.thirdParty && `by ${item.thirdParty}`}{" "}
      {isCommunicationSent && item.description
        ? descriptionRenderer[item.description]
        : ""}
      {isUpdated && item.description && `, ${item.description}`}
      {isBalanceUpdated && item.description && (
        <span className="-ml-1">, {item.description}</span>
      )}
      {isBalanceUpdated &&
        item.providerValueChanges?.length &&
        item.providerValueChanges.map((item, index) => (
          <div key={`${item.providerId}_${index}`} className="mt-1">
            {item?.provider?.name} - from{" "}
            {parsePrice(centsToDollars(item.initialValue))} to{" "}
            {parsePrice(centsToDollars(item.newValue))}
            {item.initialPriority !== undefined &&
              item.newPriority !== undefined && (
                <span>
                  , priority changed from {item.initialPriority} to{" "}
                  {item.newPriority}
                </span>
              )}
          </div>
        ))}
      {isEligibilityRerun && renderEligibilityRerunChanges(item)}
      {isCoverageInactive && item.description && `, ${item.description}`}
      {isProviderPriorityUpdated && item.description && `, ${item.description}`}
      {isProviderPriorityUpdated &&
        item.providerValueChanges?.length &&
        item.providerValueChanges.map((item, index) => (
          <div key={`${item.providerId}_${index}`} className="mt-1">
            {item?.provider?.name} - priority changed from{" "}
            {item.initialPriority ?? item.initialValue} to{" "}
            {item.newPriority ?? item.newValue}
          </div>
        ))}
      {isBalanceReported &&
        `Correct Balance: ${parsePrice(
          centsToDollars(item.amountInCents || 0)
        )}, Provider: ${item.toProvider?.name}, Notes: ${item.description}`}
      {item.note && ` Note: ${item.note}`}
      {(isCommunicationSent || item.type === "TextMessageFailed") && (
        <SentMessageBody item={item} />
      )}
      {isTransferFailed && item.description && `, Reason: ${item.description}`}
      {isTransferBlocked &&
        `Transfer Blocked${item.user?.name ? ` by ${item.user.name}` : ""}`}
      {isTransferReady &&
        `Transfer Marked Ready${
          item.user?.name ? ` by ${item.user.name}` : ""
        }`}
    </div>
  );
};

export const custimizedMarker = (item: IOrderHistory) => {
  const iconClasses: { [key: string]: string } = {
    Created: "pi pi-check",
    Updated: "pi pi-pencil",
    Canceled: "pi pi-times",
    Paid: "pi pi-dollar",
    Transfer: "pi pi-sync",
    PartiallyPaid: "pi pi-dollar",
    InstallmentPaid: "pi pi-dollar",
    EmailSent: "pi pi-envelope",
    TextMessageSent: "pi pi-comment",
    EmailResent: "pi pi-envelope",
    TextMessageResent: "pi pi-comment",
    EstimateOpened: "pi pi-eye",
    NotesAdded: "pi pi-file-edit",
    NoteToPdf: "pi pi-file-edit",
    Redone: "pi pi-refresh",
    CreatedAsDraft: "pi pi-check",
    Approved: "pi pi-check",
    PaymentMarkedBlockedForTransfers: "pi pi-ban",
    PaymentMarkedReadyForTransfers: "pi pi-check",
    PaidAdditionalAmount: "pi pi-dollar",
    BalanceUpdated: "pi pi-dollar",
    BalanceReported: "pi pi-wrench",
    NextInstallmentDateChanged: "pi pi-calendar",
    CreatedWithDirectPay: "pi pi-check",
    ProviderPriorityUpdated: "pi pi-sort-numeric-down",
    CardUpdated: "pi pi-credit-card",
    ExternalPaymentRecorded: "pi pi-file-edit",
    TextMessageFailed: "pi pi-times-circle",
    HstUpdate: "pi pi-sync",
    EligibilityRerun: "pi pi-refresh",
    TransferFailed: "pi pi-times-circle",
    CoverageInactive: "pi pi-exclamation-triangle",
  };
  const isError =
    item.type === "TextMessageFailed" || item.type === "TransferFailed";
  const markerColor = isError ? "var(--red-500)" : "var(--color-mint-5)";
  return (
    <div
      className="flex w-2rem h-2rem align-items-center justify-content-center border-2 border-circle"
      style={{
        borderColor: markerColor,
        color: markerColor,
      }}
    >
      {/* Fall back rather than render an empty circle. Three enum values the backend can already
          write have never had an entry here (Expired, Refunded, InstallmentFailed), and a newly
          added type is unmapped for the whole window between the API and SPA deploys. */}
      <i className={iconClasses[item.type] || "pi pi-info-circle"} />
    </div>
  );
};

export const typeRenderer: { [key: string]: string } = {
  PartiallyPaid: "Partially Paid",
  InstallmentPaid: "Auto Paid",
  EmailSent: "Email Sent",
  TextMessageSent: "Text Message Sent",
  EmailResent: "Email Resent",
  TextMessageResent: "Text Message Resent",
  EstimateOpened: "Estimate Opened",
  NotesAdded: "Note Added",
  NoteToPdf: "Note Added to PDF",
  CreatedAsDraft: "Created as Draft",
  PaymentMarkedBlockedForTransfers: "Transfer Blocked",
  PaymentMarkedReadyForTransfers: "Transfer Ready",
  PaidAdditionalAmount: "Paid Additional Amount",
  BalanceUpdated: "Balance Updated",
  BalanceReported: "Balance Reported",
  NextInstallmentDateChanged: "Next Installment Date Changed",
  CreatedWithDirectPay: "Created with Direct Pay",
  ProviderPriorityUpdated: "Provider Payment Priority Updated",
  CardUpdated: "Card updated",
  ExternalPaymentRecorded: "External Payment Recorded",
  TextMessageFailed: "Text Message Failed",
  HstUpdate: "HST update",
  EligibilityRerun: "Eligibility Re-run",
  TransferFailed: "Failed Transfer",
  CoverageInactive: "Insurance Reported Inactive",
};

// Mirrors PaymentDetails' editButtonDisabled: a transfer is still being
// processed when the order is paid/partially paid and the latest paid payment
// has not yet been transferred to the providers.
const isTransferBeingProcessed = (
  orderStatus?: string,
  paymentPlanBreakDown?: any
): boolean => {
  if (
    (orderStatus === orderStatusConstants.PARTIALLY_PAID ||
      orderStatus === orderStatusConstants.PAID) &&
    paymentPlanBreakDown?.payments?.length
  ) {
    const initialDeposit = paymentPlanBreakDown.payments[0];
    const paymentsWithoutInitialDeposit = paymentPlanBreakDown.payments.filter(
      (p: any) => !!p.installmentInDb
    );
    const lastPaidInstallment = paymentsWithoutInitialDeposit.reduce(
      (last: any, p: any) => (p.installmentInDb?.paymentId ? p : last),
      null
    );
    if (lastPaidInstallment) {
      return !lastPaidInstallment.transferredToProviders;
    }
    return !initialDeposit.transferredToProviders;
  }
  return false;
};

function OrderHistory({
  orderHistories,
  orderCreated,
  orderId,
  onUpdateOrder,
  patientName,
  orderPaymentNotesEnabled,
  clearPatientFeeValueInPercentage,
  orderStatus,
  providerAmounts,
  paymentPlanBreakDown,
  paymentPlan,
  paymentPlanRemaining,
  orderType,
}: {
  orderHistories: IOrderHistory[];
  orderType: OrderType;
  orderCreated: string;
  orderId: string;
  onUpdateOrder: () => void;
  patientName: string;
  orderPaymentNotesEnabled?: boolean;
  clearPatientFeeValueInPercentage?: number;
  orderStatus?: string;
  providerAmounts?: IProviderAmount[];
  paymentPlanBreakDown?: any;
  paymentPlan?: IPaymentPlan;
  paymentPlanRemaining?: Array<{
    providerId: string;
    remainingAmountInCents: number;
  }>;
}) {
  const [showMore, setShowMore] = useState(false);
  const [note, setNote] = useState("");
  const [dialogVisible, setDialogVisible] = useState(false);
  const [paymentData, setPaymentData] = useState<IPaymentNoteData>({
    isPaymentNote: false,
    paymentAmount: null,
    paymentType: null,
  });
  const [createOrderHistory] = useCreateOrderHistoryAsStaffMutation();
  const [updateOrder] = useUpdateOrderMutation();
  const { current: toastElement } = useContext(ToastContext);
  const { userInfo } = useUserWithAccount();

  const transferBeingProcessed = isTransferBeingProcessed(
    orderStatus,
    paymentPlanBreakDown
  );

  // The amount-reducing calculation / auto-reduce only applies to GFE orders.
  const isGFE = orderType === orderTypeConstants.GFE;

  // The subtotal the Clear Patient Fee was rounded up on and prorated across, so
  // a payment covering one provider can have its share of that fee stripped back
  // out at the same ratio. It has to match the amounts the reduction is taken
  // from: patient portions on the full-payment path
  // (reduceFirstProviderAndUpdateOrder), remaining-to-pay amounts on the
  // partially paid one (reduceProviderRemainingAndUpdateOrder).
  const cpfBasisInCents = useMemo(() => {
    if (orderStatus === orderStatusConstants.PARTIALLY_PAID) {
      return (paymentPlanRemaining || []).reduce(
        (acc, item) => acc + (item.remainingAmountInCents || 0),
        0
      );
    }
    return (providerAmounts || []).reduce(
      (acc, item) => acc + (item.patientPortion || 0),
      0
    );
  }, [orderStatus, paymentPlanRemaining, providerAmounts]);

  // For partially paid "Set amount" plans, block auto-reduce when the new
  // remaining amount would no longer cover a single monthly installment.
  const installmentAmountInsufficient = useMemo(() => {
    if (orderStatus !== orderStatusConstants.PARTIALLY_PAID) return false;
    if (!paymentPlan || paymentPlan.calculationBasis !== "InstallmentAmount") {
      return false;
    }
    const installment = paymentPlan.installmentAmountInCents || 0;
    if (installment <= 0) return false;
    const reduceByAmount = getProviderReduceByAmount(
      paymentData.paymentAmount,
      paymentData.paymentType,
      clearPatientFeeValueInPercentage,
      cpfBasisInCents
    );
    if (reduceByAmount == null) return false;
    const newRemaining = getReducedRemainingInCents({
      reduceByAmount,
      paymentPlanRemaining,
      clearFeePercentage: Number(
        paymentPlanBreakDown?.clearFee?.valueInPercentage
      ),
      isGFE: orderType === orderTypeConstants.GFE,
      clearPatientFeePercentage: clearPatientFeeValueInPercentage,
    });
    return newRemaining < installment;
  }, [
    orderStatus,
    paymentPlan,
    paymentData,
    clearPatientFeeValueInPercentage,
    cpfBasisInCents,
    paymentPlanRemaining,
    paymentPlanBreakDown,
    orderType,
  ]);

  // The primary provider's remaining amount to pay (cents), used to validate the
  // reduction on partially paid orders.
  const primaryProviderRemaining = useMemo(() => {
    const primaryProvider = (providerAmounts || []).find(
      (provider) => provider.specialty === SpecialtyList.PRIMARY_PROVIDER
    );
    if (!primaryProvider) return 0;
    return (
      (paymentPlanRemaining || []).find(
        (r) => r.providerId === primaryProvider.providerId
      )?.remainingAmountInCents || 0
    );
  }, [providerAmounts, paymentPlanRemaining]);

  // For partially paid orders, block auto-reduce when the reduction is larger
  // than the amount remaining to pay to the primary provider.
  const providerRemainingInsufficient = useMemo(() => {
    if (orderStatus !== orderStatusConstants.PARTIALLY_PAID) return false;
    const reduceByAmount = getProviderReduceByAmount(
      paymentData.paymentAmount,
      paymentData.paymentType,
      clearPatientFeeValueInPercentage,
      cpfBasisInCents
    );

    if (reduceByAmount == null) return false;
    return dollarsToCents(reduceByAmount) > primaryProviderRemaining;
  }, [
    orderStatus,
    paymentData,
    clearPatientFeeValueInPercentage,
    cpfBasisInCents,
    primaryProviderRemaining,
  ]);
  // When "Auto reduce" is checked and the order has not been paid yet, mirror
  // ProviderPayoutCalculations.handleSaveProviderAmounts: take the whole
  // reduction off the primary provider and recompute totalPatientPortion.
  // reducedProviderAmount is the amount to reduce the provider BY:
  // the post-Clear-Patient-Fee amount for Financing, or the full amount (100%) for Cash/Check.
  const reduceFirstProviderAndUpdateOrder = (reducedProviderAmount: number) => {
    // "First provider" = the first one with the Primary Provider specialty.
    const primaryProvider = (providerAmounts || []).find(
      (provider) => provider.specialty === SpecialtyList.PRIMARY_PROVIDER
    );
    if (!primaryProvider) {
      return Promise.resolve();
    }

    const reductionInCents = dollarsToCents(reducedProviderAmount);

    const differenceByProvider = {
      [primaryProvider.providerId]: -reductionInCents,
    };

    const currentBaseInCents = (providerAmounts || []).reduce(
      (acc, item) => acc + (item.patientPortion || 0),
      0
    );
    const newBaseInCents = currentBaseInCents - reductionInCents;

    let totalPatientPortion = newBaseInCents;
    if (
      clearPatientFeeValueInPercentage &&
      clearPatientFeeValueInPercentage > 0
    ) {
      const clearPatientFeeInDollars = calculateClearPatientFee(
        centsToDollars(newBaseInCents),
        clearPatientFeeValueInPercentage
      );
      totalPatientPortion =
        newBaseInCents + dollarsToCents(clearPatientFeeInDollars);
    }

    return updateOrder({
      id: orderId,
      providerPayout: {
        differenceByProvider,
        totalPatientPortion,
      },
    }).unwrap();
  };

  // For partially paid orders we don't touch the patient portion; instead we
  // reduce the amount remaining to pay to the primary provider, mirroring
  // PaymentDetails' remaining-amount edit which submits a paymentPlan object
  // with the new total remaining plus differenceByProvider / valuesByProvider.
  const reduceProviderRemainingAndUpdateOrder = (
    reducedProviderAmount: number
  ) => {
    const primaryProvider = (providerAmounts || []).find(
      (provider) => provider.specialty === SpecialtyList.PRIMARY_PROVIDER
    );
    if (!primaryProvider || !paymentPlan) {
      return Promise.resolve();
    }

    const reductionInCents = dollarsToCents(reducedProviderAmount);
    const { providerId } = primaryProvider;

    // Remaining (without fee) per provider comes from paymentPlanRemaining,
    // mirroring PaymentDetails' remaining breakdown (totalExpectedTransferAmount).
    const currentProviderRemaining =
      (paymentPlanRemaining || []).find((r) => r.providerId === providerId)
        ?.remainingAmountInCents || 0;

    const differenceByProvider = { [providerId]: -reductionInCents };
    const valuesByProvider = {
      [providerId]: {
        initialValue: currentProviderRemaining,
        newValue: currentProviderRemaining - reductionInCents,
      },
    };

    // New remaining total with fee re-applied, mirroring RemainingAmountEditTable.
    const newRemainingAmountInCents = getReducedRemainingInCents({
      reduceByAmount: reducedProviderAmount,
      paymentPlanRemaining,
      clearFeePercentage: Number(
        paymentPlanBreakDown?.clearFee?.valueInPercentage
      ),
      isGFE: orderType === orderTypeConstants.GFE,
      clearPatientFeePercentage: clearPatientFeeValueInPercentage,
    });

    const MAX_PAYMENT_PLAN_DURATION = 50;
    let { paymentPlanDuration } = paymentPlan;
    if (
      paymentPlan.calculationBasis === "InstallmentAmount" &&
      paymentPlan.installmentAmountInCents &&
      paymentPlan.installmentAmountInCents > 0 &&
      newRemainingAmountInCents > 0
    ) {
      // "Set amount" plans: duration is derived from remaining / installment.
      const installmentsNeeded = Math.min(
        Math.ceil(
          newRemainingAmountInCents / paymentPlan.installmentAmountInCents
        ),
        MAX_PAYMENT_PLAN_DURATION
      );
      paymentPlanDuration = Math.max(1, installmentsNeeded);
    } else if (
      newRemainingAmountInCents > 0 &&
      paymentPlan.remainingAmountInCents &&
      paymentPlan.calculationBasis !== "InstallmentAmount"
    ) {
      // "Duration" plans: adjust the duration so the monthly payment stays as
      // close as possible to the current one, mirroring PaymentDetails.
      const remainingInstallmentsCount = paymentPlan.installments?.length
        ? paymentPlan.installments.filter((i) => !i.paymentId).length
        : paymentPlan.paymentPlanDuration || 0;
      const previousMonthly =
        remainingInstallmentsCount > 0
          ? Math.round(
              paymentPlan.remainingAmountInCents / remainingInstallmentsCount
            )
          : 0;
      if (previousMonthly > 0) {
        let bestDuration = Math.max(
          1,
          Math.round(newRemainingAmountInCents / previousMonthly)
        );
        // If that would push the monthly payment more than 6% above the current
        // one, add a month to bring it back down.
        if (newRemainingAmountInCents / bestDuration > previousMonthly * 1.06) {
          bestDuration += 1;
        }
        paymentPlanDuration = Math.min(bestDuration, MAX_PAYMENT_PLAN_DURATION);
      }
    }

    // Next installment date = first unpaid installment's due date (formatted).
    const sortedInstallments = [...(paymentPlan.installments || [])].sort(
      (a, b) => new Date(a.dateDue).getTime() - new Date(b.dateDue).getTime()
    );
    const firstUnpaidInstallment = sortedInstallments.find(
      (installment) => !installment.paymentId
    );
    const nextInstallmentDate = firstUnpaidInstallment?.dateDue
      ? parseDate(firstUnpaidInstallment.dateDue, false, true)
      : "";
    console.log("newpaymentplan", {
      paymentPlan: [
        {
          paymentPlanDuration,
          active: !!paymentPlan?.active,
          hasInstallments: !!paymentPlanBreakDown?.payments?.length,
          paymentPlanMinDownPaymentFlat:
            paymentPlan.paymentPlanMinDownPaymentFlat
              ? paymentPlan.paymentPlanMinDownPaymentFlat
              : null,
          paymentPlanMinDownPaymentPercent:
            paymentPlan.paymentPlanMinDownPaymentPercent ?? null,
          providerDistributionLogic:
            orderType === orderTypeConstants.PATIENT_RESPONSIBILITY
              ? null
              : paymentPlan.providerDistributionLogic,
          differenceByProvider,
          valuesByProvider,
          installmentFrequency: paymentPlan.installmentFrequency ?? "Monthly",
          installmentAmountInCents:
            paymentPlan.installmentAmountInCents != null &&
            paymentPlan.installmentAmountInCents !== undefined
              ? paymentPlan.installmentAmountInCents
              : null,
          calculationBasis: paymentPlan?.calculationBasis ?? "Duration",
          nextInstallmentDateChanged: false,
          nextInstallmentDate,
          remainingAmountInCents: newRemainingAmountInCents,
        },
      ] as any,
    });
    return updateOrder({
      id: orderId,
      paymentPlan: [
        {
          paymentPlanDuration,
          active: !!paymentPlan?.active,
          hasInstallments: !!paymentPlanBreakDown?.payments?.length,
          paymentPlanMinDownPaymentFlat:
            paymentPlan.paymentPlanMinDownPaymentFlat
              ? paymentPlan.paymentPlanMinDownPaymentFlat
              : null,
          paymentPlanMinDownPaymentPercent:
            paymentPlan.paymentPlanMinDownPaymentPercent ?? null,
          providerDistributionLogic:
            orderType === orderTypeConstants.PATIENT_RESPONSIBILITY
              ? null
              : paymentPlan.providerDistributionLogic,
          differenceByProvider,
          valuesByProvider,
          installmentFrequency: paymentPlan.installmentFrequency ?? "Monthly",
          installmentAmountInCents:
            paymentPlan.installmentAmountInCents != null &&
            paymentPlan.installmentAmountInCents !== undefined
              ? paymentPlan.installmentAmountInCents
              : null,
          calculationBasis: paymentPlan?.calculationBasis ?? "Duration",
          nextInstallmentDateChanged: false,
          nextInstallmentDate,
          remainingAmountInCents: newRemainingAmountInCents,
        },
      ] as any,
    }).unwrap();
  };

  const orderHistoryList = useMemo(() => {
    const orderHistoryListUpdated = [...orderHistories];
    const hasCreatedType = orderHistories.some(
      (item) => item.type === "Created"
    );
    const isOldOrder = new Date(orderCreated) < new Date("2024-12-01");
    if (!hasCreatedType && isOldOrder) {
      orderHistoryListUpdated.unshift({
        id: "",
        type: "Created",
        createdAt: orderCreated,
        orderId,
      });
    }
    return orderHistoryListUpdated;
  }, [orderHistories, orderId, orderCreated]);

  const onAddNote = async (
    autoReduce?: boolean,
    reducedProviderAmount?: number | null
  ) => {
    try {
      const { isPaymentNote, paymentAmount, paymentType } = paymentData;

      const isPaid = orderStatus === orderStatusConstants.PAID;
      const isPartiallyPaid =
        orderStatus === orderStatusConstants.PARTIALLY_PAID;

      // Run the reduction first and wait for it to commit, so the single
      // refetch below reflects the reduced amounts (concurrent refetches get
      // deduped and can resolve before the reduction is persisted).
      if (isPaymentNote && autoReduce && paymentAmount != null && isGFE) {
        if (isPartiallyPaid) {
          // Reduce the amount remaining to pay to the primary provider, unless
          // the reduction can't be applied (not enough remaining, or the new
          // remaining can no longer cover a monthly installment).
          if (
            !installmentAmountInsufficient &&
            !providerRemainingInsufficient
          ) {
            await reduceProviderRemainingAndUpdateOrder(
              reducedProviderAmount ?? 0
            );
          }
        } else if (!isPaid) {
          // Reduce the primary provider's patient portion.
          await reduceFirstProviderAndUpdateOrder(reducedProviderAmount ?? 0);
        }
      }

      await createOrderHistory({
        orderId,
        type: isPaymentNote
          ? orderHsitoryTypeConstants.ExternalPaymentRecorded
          : orderHsitoryTypeConstants.NotesAdded,
        note: note?.trim()?.length ? note.trim() : undefined,
        userId: userInfo?.id,
        ...(isPaymentNote && {
          amountInCents: paymentAmount
            ? dollarsToCents(paymentAmount)
            : undefined,
          externalPaymentMethod: paymentType as
            | "Cash"
            | "Check"
            | "CareCredit"
            | undefined,
        }),
      }).unwrap();

      // Refetch once, after both the reduction and the note have been saved.
      onUpdateOrder();
      toastElement?.show({
        summary: "Success!",
        severity: "success",
        detail: "Estimate Note has been added successfully.",
      });
    } catch (error) {
      toastElement?.show({
        severity: "error",
        detail: "Try again later.",
        summary: "Something went wrong.",
      });
    }
    setDialogVisible(false);
    setNote("");
    setPaymentData({
      isPaymentNote: false,
      paymentAmount: null,
      paymentType: null,
    });
  };

  const header = () => {
    return (
      <div className="flex flex-column col-12 align-items-end">
        <div>
          <Button
            label="Add Note"
            icon="pi pi-plus-circle"
            onClick={() => {
              setDialogVisible(true);
            }}
            className="p-button-outlined-light"
          />
        </div>
      </div>
    );
  };

  return (
    <>
      <AddNoteDialog
        note={note}
        setNote={setNote}
        showDialog={dialogVisible}
        setShowDialog={setDialogVisible}
        paymentData={paymentData}
        setPaymentData={setPaymentData}
        orderPaymentNotesEnabled={orderPaymentNotesEnabled}
        clearPatientFeeValueInPercentage={clearPatientFeeValueInPercentage}
        orderStatus={orderStatus}
        providerAmounts={providerAmounts}
        transferBeingProcessed={transferBeingProcessed}
        installmentAmountInsufficient={installmentAmountInsufficient}
        providerRemainingInsufficient={providerRemainingInsufficient}
        primaryProviderRemaining={primaryProviderRemaining}
        isGFE={isGFE}
        cpfBasisInCents={cpfBasisInCents}
        onAddNote={onAddNote}
      />
      <StyledCard title="Estimate history" header={header()}>
        <StyledTimeline
          value={showMore ? orderHistoryList : orderHistoryList.slice(0, 3)}
          content={(item) => generateOrderHistoryLine(item, patientName)}
          opposite={(item) => {
            // Show "Paid via Cash" for CashUnmanaged payments
            if (
              item.type === orderHsitoryTypeConstants.ExternalPaymentRecorded &&
              item.externalPaymentMethod === "CashUnmanaged"
            ) {
              return "Paid via Cash";
            }
            return typeRenderer[item.type] || item.type;
          }}
          marker={(item) => custimizedMarker(item)}
        />
        {orderHistoryList?.length > 3 && (
          <StyledShowMore
            className="small-text purple flex align-items-center cursor-pointer mb-4"
            onClick={() => setShowMore(!showMore)}
          >
            {showMore ? (
              <>
                Show Less <i className="pi pi-angle-up ml-1" />
              </>
            ) : (
              <>
                Show More <i className="pi pi-angle-down ml-1" />
              </>
            )}
          </StyledShowMore>
        )}
      </StyledCard>
    </>
  );
}

export default OrderHistory;
